// E2/E3 My Reservations portal — the "More options" menu (E2) and the Change / Guests /
// Requests drawers (E3), ported from prototype reservations.html (openChange/openGuests/
// openRequests) onto server endpoints. Money is NEVER computed here: the Change drawer's
// "New total" comes from the Quote endpoint (the D1 engine) on every selection change
// (hard rule 10 — this file only displays).

import { createFocusTrap } from './modules/focus-trap.js';
import { startBusy, stopBusy } from './modules/busy.js';
import { createCombobox } from './modules/combobox.js';
import { renderChargesLedger } from './modules/charges-ledger.js';
import { dateHtml } from './modules/spoken-date.js';
import { announce, announcePage } from './modules/announce.js';
import { track, trackThen } from './modules/analytics.js';
import { RESERVATION_ERRORS, messageFor } from './modules/reservation-errors.js';
import { fetchPage, pageNavigation } from './modules/page-refresh.js';

/* ── sign-in stages: say what a static page cannot (a11y audit 2026-09-17) ──────
   A role="alert" that is already on the page at load is not a CHANGE, so most readers never speak
   it. The code stage autofocuses its input, which now carries aria-describedby to the error and is
   read on focus; the sign-in stage (session expired, code expired, no bookings…) has no autofocus,
   so its error goes through the site announcer. "Resend code" re-renders an identical page — the
   [data-resent] marker is how it says a NEW code went out. */
if (document.querySelector('[data-resent]')) {
  announcePage('A new sign-in code is on its way to your email.');
} else {
  const loadErr = document.querySelector('.rv-err');
  if (loadErr && !document.querySelector('[autofocus]')) announcePage(loadErr.textContent.trim());
}

/* ── "More" menus (E2 toolbar; FB91 added one per reservation card) ─────────── */
/* FB91: this was a singleton — one menu, fetched by id. Each card now carries its own, so the
   toggle is a helper over a registry and the dismissal listeners are registered ONCE at document
   level rather than one pair per card. */
const menus = [];
/* FB90: closing goes through here so the menu ITEMS dismiss it the same way its own outside-click
   and Escape do. The item handlers used to set `hidden` directly and leave aria-expanded="true"
   behind, which tells a screen reader the menu is still open after it has gone. */
const closeMenu = (m) => {
  if (m.menu.hidden) return;
  /* FB91: hand focus back to the trigger BEFORE hiding. `[hidden]` is display:none !important
     (site.css:62), so hiding a menu that still holds focus drops the user on <body> — a keyboard
     user would then Tab from the top of the page instead of from the card they were working in. */
  if (m.menu.contains(document.activeElement)) m.btn.focus();
  m.menu.hidden = true;
  m.btn.setAttribute('aria-expanded', 'false');
};
const closeAllMenus = () => menus.forEach(closeMenu);
/* The registry entry owning a given item — an action closes ITS OWN menu, never everyone's. */
const menuFor = (el) => menus.find((m) => m.menu.contains(el)) ?? null;
/* The menu's items in reading order (buttons only — separators and the View other reservations form's
   wrapper are not stops). */
const menuItems = (menu) => [...menu.querySelectorAll('button')].filter((b) => !b.disabled && !b.hidden);
const wireMenu = (btn, menu) => {
  /* A101 follow-up: wireMenus runs at load AND from initPortal — a second wiring adds a second toggle
     handler, which re-closes the menu the first one just opened. One wiring per trigger. */
  if (!btn || !menu || menus.some((m) => m.btn === btn)) return;
  menus.push({ btn, menu });
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = !menu.hidden;
    closeAllMenus();          // one open menu at a time — opening card B closes card A
    if (wasOpen) return;
    menu.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    /* ADA screen-reader review 2026-09-18: "Tab from More sometimes lands on Manage requests".
       Focus stayed on the trigger, and in a screen reader's browse mode Tab moves on from the
       READING cursor, not from the focused button — so anyone who had arrowed a line or two into
       the just-opened menu skipped its first items. Put focus (and with it the reading cursor) on
       the first item, as the menu-button pattern does; arrows move within the menu (below). */
    menuItems(menu)[0]?.focus();
  });
  menu.addEventListener('keydown', (e) => {
    const items = menuItems(menu);
    const at = items.indexOf(document.activeElement);
    const to = { ArrowDown: at + 1, ArrowUp: at - 1, Home: 0, End: items.length - 1 }[e.key];
    if (to === undefined || items.length === 0) return;
    e.preventDefault();
    items[(to + items.length) % items.length].focus();
  });
};
/* A101: runs at load and again after the cards are refreshed in place — entries whose menu left the page
   are dropped first, so the registry never holds a detached node. */
function wireMenus(root) {
  menus.splice(0, menus.length, ...menus.filter((m) => m.menu.isConnected));
  wireMenu(root.querySelector('#rvMoreBtn'), root.querySelector('#rvMoreMenu'));
  root.querySelectorAll('.rv-res .rv-more').forEach((wrap) =>
    wireMenu(wrap.querySelector('button[aria-controls]'), wrap.querySelector('.rv-moremenu')));
}
wireMenus(document);
document.addEventListener('click', (e) => {
  menus.forEach((m) => { if (!m.menu.hidden && !m.menu.contains(e.target)) closeMenu(m); });
});
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const open = menus.find((m) => !m.menu.hidden);
  if (!open) return;
  closeMenu(open);
  open.btn.focus();           // back to ITS OWN trigger, not whichever menu was wired first
});

/* ── E3 drawers ────────────────────────────────────────────────────────────── */
/* A101: the portal's wiring is a function, so a page that arrived anonymous (the sign-in form) can take the
   portal in when the attendee signs in through the chat, without a reload. Runs at most once per page. */
function initPortal() {
const portal = document.querySelector('[data-rv-portal]');
const drawer = document.querySelector('[data-rv-drawer]');
const backdrop = document.querySelector('[data-rv-backdrop]');

if (!(portal && drawer && backdrop)) return false;
{
  const drawerTitle = document.getElementById('rvDrawerTitle');
  const drawerBody = drawer.querySelector('[data-rv-drawer-body]');
  const drawerStatus = drawer.querySelector('[data-rv-drawer-status]');
  const trap = createFocusTrap(drawer);
  const token = document.querySelector('input[name="__RequestVerificationToken"]')?.value ?? '';
  const base = window.location.pathname; // /{eventGuid}/reservations

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money2 = (n) => '$' + Number(n).toLocaleString('en-US',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const openDrawer = (title, bodyHTML, { readBody = false } = {}) => {
    drawerTitle.textContent = title;
    drawerBody.innerHTML = bodyHTML;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    backdrop.classList.add('open');
    document.body.style.overflow = 'hidden';
    drawerBody.scrollTop = 0;
    // Explicit initial focus (the established drawer convention): the trap's auto-pick can't
    // see into a position:fixed container (offsetParent is null for its descendants).
    // ADA (user, 2026-10-21): a read-only drawer (the charges ledger) focuses its body instead, so
    // a screen reader reads the figures from the top as it opens; forms keep the Close button.
    if (readBody) drawerBody.tabIndex = -1;
    else drawerBody.removeAttribute('tabindex');
    trap.activate(readBody ? drawerBody : drawer.querySelector('[data-rv-drawer-close]'));
  };
  const closeDrawer = () => {
    trap.deactivate(); // restore focus BEFORE inert would silently swallow it
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    backdrop.classList.remove('open');
    document.body.style.overflow = '';
  };
  drawer.querySelector('[data-rv-drawer-close]').addEventListener('click', closeDrawer);
  backdrop.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) closeDrawer();
  });

  /* toast (prototype's rv-toast) — and the page's status live region. Every successful edit here
     (dates/room, guests, requests, contact info) reports itself ONLY through this toast, so it is
     also the entire success announcement a screen-reader user gets. */
  let toastTimer = null;
  let toastShow = null;
  /**
   * @param {string} msg
   * @param {number} [delay] ms to wait before writing the text — see below. The default suits an
   *   in-page save; the ?saved= reload needs longer.
   */
  const toast = (msg, delay = 60, { silent = false } = {}) => {
    let t = document.getElementById('rvToast');
    // Reservations.cshtml server-renders this empty, which is what makes the announcement work —
    // the same rule modules/busy.js already follows for its pill ("the live region lives
    // permanently in _Layout.cshtml"). The fallback stays for any host that doesn't (and still
    // beats nothing).
    if (!t) {
      document.body.insertAdjacentHTML('beforeend',
        '<div class="rv-toast" id="rvToast" role="status" aria-live="polite" aria-atomic="true"></div>');
      t = document.getElementById('rvToast');
    }
    clearTimeout(toastShow);
    clearTimeout(toastTimer);
    // The text goes in on a LATER task than the one that emptied (or created) the region. A live
    // region is only announced when a screen reader observes it CHANGE, so text that is already
    // present when the region is first seen — or written in the same tick it was inserted — is
    // skipped silently. Clearing first also means repeating the SAME message announces again
    // instead of looking like no change at all. The .rv-toast--on class rides along with the text
    // so the pill never fades in empty.
    t.textContent = '';
    // silent: the message is already being spoken another way (the post-reload focus landing).
    t.setAttribute('aria-live', silent ? 'off' : 'polite');
    toastShow = setTimeout(() => {
      t.textContent = msg;
      t.classList.add('rv-toast--on');
      toastTimer = setTimeout(() => t.classList.remove('rv-toast--on'), 3400);
    }, delay);
  };

  // A save reloads the portal (server re-renders fresh cards); the toast survives via ?saved=.
  const params = new URLSearchParams(window.location.search);
  if (params.has('saved')) {
    /* ADA screen-reader review 2026-09-18, third pass — "changes in My Reservations still aren't
       announced; after the save the reader just starts reading the main area". What failed:
         1. The polite toast: on a fresh page the reader is reading the page out, and polite
            messages queue behind that read — effectively never heard.
         2. A focus move (to the card heading, with the message in its name) made after `load`:
            by then the reader is mid-read and, in the user's reader, did not speak it.
       What a reader DOES reliably do on arrival is read the page — so the confirmation is now
       PART of the page before it reads it: an sr-only line inserted as the first thing in <main>,
       now, during module evaluation (before DOMContentLoaded, so it is in the document the reader
       builds). Wherever the reader starts in main, this is the first thing it says. After load,
       focus is also moved onto that line (tabindex -1), which speaks it straight away in readers
       that honour the move; Tab from it continues into the page. The line goes away once focus
       leaves it, and the visual toast is silent so nothing is said twice. */
    const msg = params.get('saved');
    params.delete('saved');
    const clean = params.toString();
    window.history.replaceState(null, '', base + (clean ? '?' + clean : ''));

    const main = document.getElementById('main');
    const note = document.createElement('p');
    note.className = 'sr-only';
    note.id = 'rvSavedNote';
    note.tabIndex = -1;
    note.textContent = msg.endsWith('.') ? msg : `${msg}.`;
    if (main) main.prepend(note);
    toast(msg, 60, { silent: !!main });

    const land = () => {
      if (!note.isConnected) return;
      note.addEventListener('blur', () => note.remove(), { once: true });
      note.focus({ preventScroll: true });
    };
    const settle = () => window.setTimeout(land, 300);
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
  }
  /* Set by the two paths that end this page by navigating. Everything on this page that shows busy
     feedback runs inside a try/finally that calls stopBusy() — and a save's finally fires AFTER
     reloadWithToast has already started the busy state for the reload it just kicked off. Without
     this flag the teardown wins the race and the navigation runs with no feedback at all, which is
     precisely the wait people notice most. Never cleared: the page is leaving. */
  let navigating = false;
  // G4 #3: `analytics` ({ event, data }) is pushed first and the navigation waits for GTM to take
  // it (trackThen) — otherwise the reload would race the push and the portal action vanish.
  const reloadWithToast = (msg, analytics) => {
    navigating = true;
    document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label: 'Saving your changes…' } }));
    const go = () => { window.location = base + '?saved=' + encodeURIComponent(msg); };
    if (analytics) trackThen(analytics.event, analytics.data, go); else go();
  };
  const modified = (change) => ({ event: 'xp_ev_reservation_modify', data: { change } });

  // FB34: 401 = the 30-min sliding sign-in cookie lapsed mid-visit. Every drawer fetch funnels
  // through here, so one redirect (the sign-in stage explains itself via ?expired=1) covers all.
  const sessionExpired = () => {
    navigating = true;
    document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label: 'Signing you back in…' } }));
    window.location = base + '?expired=1';
  };
  const getJson = async (url) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (res.status === 401) sessionExpired();
    if (!res.ok) throw Object.assign(new Error('fetch failed'), { status: res.status });
    return res.json();
  };
  const postJson = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', RequestVerificationToken: token },
      body: body === undefined ? '{}' : JSON.stringify(body),
    });
    if (res.status === 401) sessionExpired();
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  };

  // H3b: the copy map lives in modules/reservation-errors.js, shared with the booking assistant.
  const ERRORS = RESERVATION_ERRORS;
  const errorText = (data) => messageFor(data);

  /* ── drawer errors (a11y audit 2026-09-17) ──────────────────────────────────
     The drawer error boxes used to be plain divs un-hidden and written in the same tick, while the
     Save button had just been `disabled` — so focus was on <body> and the failure was never spoken.
     Now: the box is revealed EMPTY, takes focus (tabindex="-1", reads nothing yet), and announce()
     writes the text on a later task so the role="alert" fires. The box must stay `hidden` while
     empty because .rv-newtotal is a padded, bordered panel — visible-and-empty would draw a blank
     red box. Save buttons are no longer disabled for the round trip: an in-flight flag guards the
     double click and busy.js's aria-disabled + spinner carry the feedback. */
  const showErr = (el, msg) => {
    if (!el) return;
    el.hidden = false;
    el.focus();
    announce(el, msg);
  };
  const hideErr = (el) => { if (el) { el.hidden = true; el.textContent = ''; } };
  /* True (and marks it) when this control is already carrying a round trip. */
  const beginInflight = (btn) => {
    if (btn.dataset.inflight) return false;
    btn.dataset.inflight = '1';
    return true;
  };
  const endInflight = (btn) => { delete btn.dataset.inflight; };

  const dayLabel = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const listDays = (startIso, endIso) => {
    const days = [];
    for (let d = new Date(startIso + 'T00:00:00'), end = new Date(endIso + 'T00:00:00'); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  };

  /* ── Change drawer ──────────────────────────────────────────────────────── */
  const openChange = async (resId) => {
    let info;
    try {
      info = await getJson(`${base}?handler=ModifyInfo&id=${resId}`);
    } catch {
      toast('Could not load this reservation — refresh and try again.');
      return;
    }
    const r = info.reservation;
    if (!r.canModify) {
      toast(ERRORS.ModifyCutoff);
      return;
    }

    const days = listDays(info.window.start, info.window.end);
    const ciOpts = days.slice(0, -1).map((d) =>
      `<option value="${d}"${d === r.checkin ? ' selected' : ''}>${dayLabel(d)}</option>`).join('');
    const coOpts = days.slice(1).map((d) =>
      `<option value="${d}"${d === r.checkout ? ' selected' : ''}>${dayLabel(d)}</option>`).join('');

    openDrawer('Change reservation ' + r.conf, `
      <div class="rv-form">
        ${r.isPromoActive ? `<div class="rv-newtotal rv-newtotal--warn"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          Modifying your stay dates may impact the promotional rates for your booking.</div>` : ''}
        <div class="bk-occ-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i> Changing hotel, room, or dates re-prices the stay at current event rates. Nothing is charged until you save.</div>
        <label class="bk-fieldlabel" for="chHotel">Hotel</label>
        <select id="chHotel">${info.hotels.map((h) =>
          `<option value="${Number(h.id)}"${h.id === r.hotelId ? ' selected' : ''}>${esc(h.name)}${h.available ? '' : ' — limited availability'}</option>`).join('')}</select>
        <label class="bk-fieldlabel" for="chRoom">Room type</label>
        <select id="chRoom">${info.rooms.map((rt) =>
          `<option value="${Number(rt.id)}"${rt.id === r.roomTypeId ? ' selected' : ''}>${esc(rt.name)}${rt.beds ? ` · ${Number(rt.beds)} bed${rt.beds !== 1 ? 's' : ''}` : ''}</option>`).join('')}</select>
        <div class="rv-form-2col">
          <div><label class="bk-fieldlabel" for="chCi">Check-in</label><select id="chCi">${ciOpts}</select></div>
          <div><label class="bk-fieldlabel" for="chCo">Check-out</label><select id="chCo">${coOpts}</select></div>
        </div>
        <div class="rv-newtotal" id="chTotal" tabindex="-1"></div>
        <div class="rv-form-foot">
          <button class="book-btn rv-btn" id="chSave" type="button">Save changes</button>
          <button class="rv-btn rv-btn--ghost" id="chDrop" type="button">Discard</button>
        </div>
      </div>`);

    const $ = (id) => document.getElementById(id);
    let quoteSeq = 0;
    let roomSeq = 0;

    /* 2026-08-21: room ids are PER HOTEL here (event_rmtp contracts a distinct id_rmtp to each
       hotel) — unlike the prototype's one global ROOM_TYPES list this drawer was ported from. So
       carrying the selected id across a hotel switch can NEVER match, and the select silently fell
       back to whichever room sorts first: leaving hotel A and coming back re-priced the stay
       against a room the visitor never picked, showing a "New total" (and a deposit difference) for
       a selection that looked unchanged. Remember the choice PER hotel instead — seeded with the
       reservation's own hotel+room, so returning to the booked hotel returns to the booked room. */
    const roomByHotel = new Map([[String(r.hotelId), String(r.roomTypeId)]]);
    const roomsByHotel = new Map([[String(r.hotelId), info.rooms]]);
    let hotelId = String(r.hotelId);

    /* A hotel the visitor has NOT been to yet has nothing remembered, so "the same room" has to be
       inferred from the name: there is no structured bed-type column (rmtp carries nbr_beds, a
       COUNT, and max_occ — the type lives in the free-text cd_rmtp; ebk.htl_GetRoomTypes.sql calls
       this out as a known gap). Deliberately conservative: an exact name match, else a shared
       bed-type word with the bed count only breaking ties. Bed count ALONE is not a match — every
       1-bed room would pair with every other, so "Corner Tower Suite" would land on "Fountain View
       King", which is worse than the hotel's first room. No signal → first room, as before. */
    const BED_WORDS = ['king', 'queen', 'double', 'twin', 'single', 'studio', 'suite'];
    const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const bedWordsOf = (rt) => norm(rt.name).split(' ').filter((w) => BED_WORDS.includes(w));
    const kindScore = (from, rt) => {
      if (norm(from.name) === norm(rt.name)) return 100;
      const want = new Set(bedWordsOf(from));
      const shared = bedWordsOf(rt).filter((w) => want.has(w)).length;
      return shared === 0 ? 0 : shared * 10 + (Number(from.beds) === Number(rt.beds) ? 5 : 0);
    };
    const closestKind = (from, rooms) => rooms.reduce(
      (best, rt) => (kindScore(from, rt) > (best ? kindScore(from, best) : 0) ? rt : best), null);

    const loadRooms = async () => {
      const next = $('chHotel').value;
      // The room being left, captured BEFORE the fetch replaces the options.
      const leaving = (roomsByHotel.get(hotelId) ?? []).find((rt) => String(rt.id) === $('chRoom').value);
      const seq = ++roomSeq;
      const data = await getJson(`${base}?handler=ModifyRooms&id=${resId}&hotelId=${next}`);
      if (seq !== roomSeq) return; // a newer hotel superseded this list — don't paint B's rooms under C
      const sel = $('chRoom');
      sel.innerHTML = data.rooms.map((rt) =>
        `<option value="${Number(rt.id)}">${esc(rt.name)}${rt.beds ? ` · ${Number(rt.beds)} bed${rt.beds !== 1 ? 's' : ''}` : ''}</option>`).join('');
      roomsByHotel.set(next, data.rooms);
      const match = leaving && !roomByHotel.has(next) ? closestKind(leaving, data.rooms) : null;
      const want = roomByHotel.get(next) ?? (match ? String(match.id) : null);
      if (want && [...sel.options].some((o) => o.value === want)) sel.value = want;
      hotelId = next;
      roomByHotel.set(hotelId, sel.value); // first visit to a hotel remembers what it landed on
    };

    /* ADA screen-reader review 2026-09-18: every re-price is spoken as ONE message in the drawer's
       status — what changed, then the result. #chTotal used to be the live region itself, which
       read the "Checking availability…" placeholder, then (without aria-atomic) only the fragment
       of the result that differed, and said nothing about what the form had changed on its own:
       a check-out pushed forward to keep one night, or a room type reset by a hotel switch. The
       select the user moved announces its own new value; this covers the consequences. */
    // Glyphs become pauses; the breakdown line (its own block) starts its own sentence.
    const words = (s) => s.replace(/\s+/g, ' ').replace(/ · /g, ', ').trim();
    const spoken = (el) => {
      const parts = el.querySelector('.rv-newtotal-parts');
      const head = [...el.childNodes].filter((n) => n !== parts).map((n) => n.textContent).join('');
      return words(parts ? `${words(head)}. ${parts.textContent}` : head);
    };
    const optionText = (sel) => words(sel.options[sel.selectedIndex]?.text ?? '');

    /** @param {'dates'|'hotel'|'room'|undefined} what  which control triggered this re-price */
    const reprice = async (what) => {
      let ci = $('chCi').value;
      let co = $('chCo').value;
      const lead = [];
      if (what === 'hotel') lead.push(`Room type ${optionText($('chRoom'))}.`);
      if (co <= ci) {
        const next = days[days.indexOf(ci) + 1];
        $('chCo').value = co = next;
        lead.push(`Check-out moved to ${dayLabel(co)}.`);
      }
      if (what === 'dates') lead.push(`Stay ${dayLabel(ci)} to ${dayLabel(co)}.`);
      const say = () => announce(drawerStatus, [...lead, spoken($('chTotal'))].join(' '));
      const total = $('chTotal');
      total.classList.remove('rv-newtotal--warn');
      total.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> Checking availability…';
      $('chSave').disabled = true;

      const seq = ++quoteSeq;
      const { ok, data } = await postJson(
        `${base}?handler=Quote&id=${resId}&hotel=${$('chHotel').value}&room=${$('chRoom').value}&ci=${ci}&co=${co}`);
      if (seq !== quoteSeq) return; // a newer selection superseded this quote
      if (!ok) {
        total.classList.add('rv-newtotal--warn');
        total.innerHTML = `<i class="fa-solid fa-calendar-xmark" aria-hidden="true"></i> ${esc(errorText(data))}`;
        say();
        return;
      }
      if (!data.available) {
        total.classList.add('rv-newtotal--warn');
        total.innerHTML = '<i class="fa-solid fa-calendar-xmark" aria-hidden="true"></i> Some of those nights are sold out for that room — try different dates.';
        say();
        return;
      }
      const diff = data.depositDifference;
      const diffNote = diff > 0
        ? ` · deposit increases ${money2(diff)}`
        : diff < 0 ? ` · deposit decreases ${money2(-diff)}` : '';
      /* 2026-08-21: "New total" only when the selection actually differs from the booked stay.
         The drawer opens on the reservation's own hotel/room/dates and quotes them, so the
         untouched drawer — and any round trip back to the booked selection — was announcing a
         "New total" for a change nobody made. The quote is a live re-price either way (the engine
         runs unconditionally; only the DEPOSIT is guarded by ModifyRules), so the unchanged label
         stays the neutral "Total" rather than claiming this is what was charged at booking. */
      const unchanged = $('chHotel').value === String(r.hotelId)
        && $('chRoom').value === String(r.roomTypeId)
        && ci === r.checkin && co === r.checkout;
      /* 2026-08-21: the headline total alone could not explain itself. Moving dates or hotels
         swings it, and "New total $1,412.00" gives the attendee no way to tell whether the swing
         is a different nightly rate or a different hotel's tax rate — the ambiguity behind the
         "why is my total not the rate I saw" support call. The quote already carries RoomCharges
         (GetModifyQuoteQuery.ModifyQuote), so this is display only; the headline line is left
         untouched so the Total/New total label, the deposit note and every rv-newtotal--warn path
         that replaces this box wholesale keep working exactly as before.
         Taxes are DERIVED by subtraction, never summed from the tax lines: PricingEngine
         faithfully reproduces a live quirk where a NULL per_night row displays as a line but
         joins neither total, so line-summing can legitimately fail to reconcile. Subtraction
         cannot disagree with the total it is explaining. Labelled to match the checkout rail
         ("Taxes & fees") — the wording the attendee saw when they booked. */
      const parts = data.roomCharges != null
        ? `<div class="rv-newtotal-parts">Room charges <strong>${money2(data.roomCharges)}</strong>
             · Taxes &amp; fees <strong>${money2(Math.round((data.totalWithTaxes - data.roomCharges) * 100) / 100)}</strong></div>`
        : '';
      total.innerHTML = `${unchanged ? 'Total' : 'New total'} <strong>${money2(data.totalWithTaxes)}</strong> incl. taxes &amp; fees · ${data.nights} night${data.nights !== 1 ? 's' : ''}${diffNote}${parts}`;
      $('chSave').disabled = false;
      say();
    };

    $('chHotel').addEventListener('change', async () => { await loadRooms(); reprice('hotel'); });
    $('chRoom').addEventListener('change', () => { roomByHotel.set(hotelId, $('chRoom').value); reprice('room'); });
    ['chCi', 'chCo'].forEach((id) => $(id).addEventListener('change', () => reprice('dates')));
    reprice();
    $('chDrop').addEventListener('click', closeDrawer);
    $('chSave').addEventListener('click', async () => {
      /* The drawer commit buttons are text-only, so `disabled` used to be their ONLY feedback: a
         greyed button and nothing else for the whole round trip. busy.js overlays a spinner on the
         label without changing the box, and escalates if the save is slow. The button is NOT
         disabled any more — that dropped focus to <body> (a11y audit 2026-09-17); the in-flight
         flag is the double-click guard. The success path navigates and must KEEP the busy state
         (see `navigating`). */
      const save = $('chSave');
      if (!beginInflight(save)) return;
      startBusy({ label: 'Saving your changes…', origin: save });
      const { ok, data } = await postJson(
        `${base}?handler=Change&id=${resId}&hotel=${$('chHotel').value}&room=${$('chRoom').value}&ci=${$('chCi').value}&co=${$('chCo').value}`);
      if (!ok) {
        stopBusy();
        endInflight(save);
        const total = $('chTotal');
        total.classList.add('rv-newtotal--warn');
        // Spoken through the drawer status (#chTotal is no longer a live region); focus lands on
        // the box too, so the keyboard is at the reason rather than on <body>.
        total.innerHTML = `<i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i> ${esc(errorText(data))}`;
        total.focus();
        announce(drawerStatus, spoken(total));
        return;
      }
      reloadWithToast(`Reservation ${data.acknowledgementNumber} updated — a revised confirmation is on its way`, modified('stay'));
    });
  };

  /* ── Guests drawer ──────────────────────────────────────────────────────── */
  const openGuests = async (resId) => {
    let info;
    try {
      info = await getJson(`${base}?handler=ModifyInfo&id=${resId}`);
    } catch {
      toast('Could not load this reservation — refresh and try again.');
      return;
    }
    const r = info.reservation;
    if (!r.canModify) {
      toast(ERRORS.ModifyCutoff);
      return;
    }
    const primary = r.occupants[0] ?? { id: 0, first: '', last: '', email: '' };
    const extras = r.occupants.slice(1);
    const cap = Math.max(0, (r.maxOccupancy || 1) - 1);

    // FB31: additional occupants carry their own arrive/depart inside the stay (occupants
    // dt_checkin/checkout — matches checkout's per-occupant dates); no email is collected.
    // FB96: those two selects are rendered `hidden`, matching checkout — booking no longer collects
    // a per-occupant window, so offering one only on modify would let a stay be edited into a shape
    // it could not be booked in. They still POST: an occupant that ALREADY has a partial window
    // keeps it (the select is pre-selected from o.ci/o.co), a new one defaults to the full stay.
    const stayDays = listDays(r.checkin, r.checkout);
    const inOpts = (sel) => stayDays.slice(0, -1).map((d) =>
      `<option value="${d}"${d === sel ? ' selected' : ''}>${dayLabel(d)}</option>`).join('');
    const outOpts = (sel) => stayDays.slice(1).map((d) =>
      `<option value="${d}"${d === sel ? ' selected' : ''}>${dayLabel(d)}</option>`).join('');

    // 2026-08-21: an additional occupant must be NAMED. The "*" labels, aria-required and the
    // .bk-missing highlight are checkout's exact affordances (site.css owns both, and .bk-float
    // already styles the label for a missing input) — the two places a guest can be added now state
    // and enforce the same rule.
    // Rows are NUMBERED (a11y audit 2026-09-17), and numbered the way the error message counts —
    // the primary occupant is 1, so the first added row is "Occupant 2". Every row used to read
    // "Occupant first name… Remove occupant" identically, and "Occupant 2 needs a first and last
    // name" pointed at nothing a screen reader could find. renumber() keeps the labels honest after
    // a removal. aria-describedby="cgErr": the row-level error is read with the field it names.
    const occRow = (o, n) => `<div class="rv-occrow rv-occrow--dated" data-occ-id="${Number(o.id) || 0}">
        <span class="bk-float"><input type="text" placeholder=" " aria-required="true" aria-describedby="cgErr" aria-label="Occupant ${n} first name (required)" value="${esc(o.first)}"><label aria-hidden="true">First *</label></span>
        <span class="bk-float"><input type="text" placeholder=" " aria-required="true" aria-describedby="cgErr" aria-label="Occupant ${n} last name (required)" value="${esc(o.last)}"><label aria-hidden="true">Last *</label></span>
        <label class="rv-occ-date" hidden><span>Arrives</span><select class="rv-occ-in" aria-label="Occupant ${n} check-in">${inOpts(o.ci || r.checkin)}</select></label>
        <label class="rv-occ-date" hidden><span>Departs</span><select class="rv-occ-out" aria-label="Occupant ${n} check-out">${outOpts(o.co || r.checkout)}</select></label>
        <button type="button" class="rv-occ-x" aria-label="Remove occupant ${n}"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </div>`;
    const occRows = () => [...drawerBody.querySelectorAll('#cgOccs .rv-occrow')];
    const renumber = () => occRows().forEach((row, i) => {
      const n = i + 2;
      const [first, last] = row.querySelectorAll('input');
      first?.setAttribute('aria-label', `Occupant ${n} first name (required)`);
      last?.setAttribute('aria-label', `Occupant ${n} last name (required)`);
      row.querySelector('.rv-occ-in')?.setAttribute('aria-label', `Occupant ${n} check-in`);
      row.querySelector('.rv-occ-out')?.setAttribute('aria-label', `Occupant ${n} check-out`);
      row.querySelector('.rv-occ-x')?.setAttribute('aria-label', `Remove occupant ${n}`);
    });

    openDrawer('Guests · ' + r.conf, `
      <div class="rv-form">
        <div class="rv-form-sec">Primary occupant</div>
        <div class="rv-form-2col" data-primary data-occ-id="${Number(primary.id) || 0}">
          <span class="bk-float"><input type="text" id="cgFirst" aria-label="Primary occupant first name" placeholder=" " value="${esc(primary.first)}"><label aria-hidden="true">First</label></span>
          <span class="bk-float"><input type="text" id="cgLast" aria-label="Primary occupant last name" placeholder=" " value="${esc(primary.last)}"><label aria-hidden="true">Last</label></span>
        </div>
        <div class="rv-form-sec">Additional occupants <span class="rv-form-cap">(up to ${cap})</span></div>
        <div id="cgOccs">${extras.map((o, i) => occRow(o, i + 2)).join('')}</div>
        <button type="button" class="bk-linkbtn" id="cgAdd"><i class="fa-solid fa-plus" aria-hidden="true"></i> Add an occupant</button>
        <div class="rv-newtotal rv-newtotal--warn" id="cgErr" role="alert" tabindex="-1" hidden></div>
        <div class="rv-form-foot">
          <button class="book-btn rv-btn" id="cgSave" type="button">Save changes</button>
          <button class="rv-btn rv-btn--ghost" id="cgDrop" type="button">Discard</button>
        </div>
      </div>`);

    const $ = (id) => document.getElementById(id);
    // FB30: the add control exists only while the room has headroom — at sleeping capacity it
    // hides instead of toasting a refusal.
    const syncAdd = () => {
      $('cgAdd').hidden = drawerBody.querySelectorAll('#cgOccs .rv-occrow').length >= cap;
    };
    // The missing-name message names specific rows ("Occupants 2, 3"), so it must not outlive the
    // state it describes: removing a row renumbers the rest, and typing into one answers part of it.
    // Both clear it — the Save re-validates from scratch, so nothing is lost by dropping it early.
    const clearOccError = () => hideErr($('cgErr'));
    const wireRows = () => drawerBody.querySelectorAll('#cgOccs .rv-occrow').forEach((row) => {
      row.querySelector('.rv-occ-x').onclick = () => {
        // Removing the row the focused button sits in drops focus to <body>; hand it to the add
        // control (re-shown by syncAdd), else the last remaining row, else the primary field.
        row.remove(); syncAdd(); clearOccError(); renumber();
        const rows = occRows();
        const next = !$('cgAdd').hidden ? $('cgAdd') : rows.length ? rows[rows.length - 1].querySelector('input') : $('cgFirst');
        next?.focus();
      };
      // The missing-name highlight clears as soon as the objection is answered — matching
      // checkout.js, where a refused field un-highlights on input rather than at the next submit.
      row.querySelectorAll('input').forEach((input) => {
        input.oninput = () => {
          if (input.value.trim()) {
            input.classList.remove('bk-missing');
            input.removeAttribute('aria-invalid');
            clearOccError();
          }
        };
      });
      // depart must follow arrive — mirror checkout's auto-correct
      row.querySelector('.rv-occ-in').onchange = (e) => {
        const out = row.querySelector('.rv-occ-out');
        if (out.value <= e.target.value) {
          out.value = stayDays[stayDays.indexOf(e.target.value) + 1];
        }
      };
    });
    wireRows();
    syncAdd();
    $('cgAdd').addEventListener('click', () => {
      const count = occRows().length;
      if (count >= cap) return;
      $('cgOccs').insertAdjacentHTML('beforeend', occRow({ id: 0, first: '', last: '' }, count + 2));
      wireRows();
      syncAdd();
      // Land in the new row's first name — the field the press was for, and the rescue when
      // syncAdd just hid this button at capacity (a hidden control drops focus to <body>).
      $('cgOccs').lastElementChild?.querySelector('input')?.focus();
    });
    $('cgDrop').addEventListener('click', closeDrawer);
    $('cgSave').addEventListener('click', async () => {
      const occupants = [{
        id: primary.id,
        first: $('cgFirst').value.trim() || primary.first,
        last: $('cgLast').value.trim() || primary.last,
        email: primary.email ?? '',
      }];
      // A row with a missing name is a HARD ERROR (2026-08-21), not a silent drop. The old
      // `if (first || last)` quietly discarded a wholly blank row and let a half-filled one through
      // to a generic server "bad-spec" (UpdateReservationValidator has always required both names) —
      // neither told the visitor which guest was wrong. Every row is checked in ONE pass so a save
      // reports all of them at once, and the "x" remains the way to drop a row typed by mistake.
      const rows = [...drawerBody.querySelectorAll('#cgOccs .rv-occrow')];
      const incomplete = [];
      rows.forEach((row, index) => {
        const inputs = [...row.querySelectorAll('input')];
        const [first, last] = inputs.map((i) => i.value.trim());
        inputs.forEach((input, field) => {
          const empty = !(field === 0 ? first : last);
          input.classList.toggle('bk-missing', empty);
          if (empty) {
            input.setAttribute('aria-invalid', 'true');
          } else {
            input.removeAttribute('aria-invalid');
          }
        });
        if (!first || !last) {
          incomplete.push({ row, number: index + 2, input: inputs.find((i) => !i.value.trim()) });
          return;
        }
        occupants.push({
          id: Number(row.dataset.occId) || 0, first, last,
          ci: row.querySelector('.rv-occ-in').value,
          co: row.querySelector('.rv-occ-out').value,
        });
      });
      if (incomplete.length) {
        const err = $('cgErr');
        // Focus the blank field FIRST (its describedby points here while the box is still empty),
        // then let the alert fire with the text — one announcement, not two.
        incomplete[0].input?.focus();
        err.hidden = false;
        announce(err, incomplete.length === 1
          ? `Occupant ${incomplete[0].number} needs a first and last name.`
          : `Occupants ${incomplete.map((o) => o.number).join(', ')} each need a first and last name.`);
        return;
      }
      hideErr($('cgErr'));
      const save = $('cgSave');
      if (!beginInflight(save)) return;
      startBusy({ label: 'Saving your guests…', origin: save });
      const { ok, data } = await postJson(`${base}?handler=Guests&id=${resId}`, { occupants });
      if (!ok) {
        stopBusy();
        endInflight(save);
        showErr($('cgErr'), errorText(data));
        return;
      }
      reloadWithToast(`Reservation ${data.acknowledgementNumber} guests updated`, modified('guests'));
    });
  };

  /* ── Requests drawer ────────────────────────────────────────────────────── */
  // Feedback 2026-08-12: the room-preference list is simplified to bed count "for now". The full
  // prior vocabulary is preserved as LEGACY_PREFS (and recorded in PLAN.md for the future
  // enhancement that restores it). Checkout (Checkout.cshtml) still OFFERS the old list, so a
  // booking made there can carry a legacy token — KNOWN_PREFS keeps parseComment recognizing those
  // so an existing reservation still shows its stored preference here rather than dropping it into
  // the free-text remainder, and the drawer surfaces an off-list current value as its own option.
  const PREFS = ['1 bed', '2 beds'];
  const LEGACY_PREFS = ['King bed', 'Two queens', 'High floor', 'Low floor', 'Near elevator', 'Quiet room'];
  const KNOWN_PREFS = [...PREFS, ...LEGACY_PREFS];
  const ADAS = ['Accessible bathroom', 'In-room accessibility', 'Roll-in shower'];

  // The D5 comment packing is "pref, ada…, other" — parse by exact vocabulary token; anything
  // unrecognized is the free-text remainder.
  const parseComment = (comment) => {
    const parts = String(comment ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    const state = { pref: '', ada: [], other: [] };
    parts.forEach((p) => {
      if (KNOWN_PREFS.includes(p) && !state.pref) state.pref = p;
      else if (ADAS.includes(p)) state.ada.push(p);
      else state.other.push(p);
    });
    return { ...state, other: state.other.join(', ') };
  };

  const openRequests = async (resId) => {
    let info;
    try {
      info = await getJson(`${base}?handler=ModifyInfo&id=${resId}`);
    } catch {
      toast('Could not load this reservation — refresh and try again.');
      return;
    }
    const r = info.reservation;
    if (!r.canModify) {
      toast(ERRORS.ModifyCutoff);
      return;
    }
    const cur = parseComment(r.comment);

    openDrawer('Requests · ' + r.conf, `
      <div class="rv-form">
        <div class="bk-occ-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i> Passed to the hotel with your reservation — honored where possible, not guaranteed.</div>
        <label class="bk-fieldlabel" for="rqLoyalty">Hotel loyalty / rewards #</label>
        <input type="text" id="rqLoyalty" maxlength="255" placeholder="Hotel loyalty / rewards number" value="${esc(r.loyalty ?? '')}" aria-label="Hotel loyalty or rewards number">
        <label class="bk-fieldlabel" for="rqPref">Room preference</label>
        <select id="rqPref"><option value="">No preference</option>${
          (cur.pref && !PREFS.includes(cur.pref) ? [cur.pref, ...PREFS] : PREFS).map((p) =>
          `<option${cur.pref === p ? ' selected' : ''}>${esc(p)}</option>`).join('')}</select>
        <label class="bk-fieldlabel" id="rqAdaLbl">Accessibility</label>
        <div class="bk-ada" role="group" aria-labelledby="rqAdaLbl">
          ${ADAS.map((a) => `<label><input type="checkbox" value="${a}"${cur.ada.includes(a) ? ' checked' : ''}> ${a}</label>`).join('')}
        </div>
        <label class="bk-fieldlabel" for="rqOther">Other requests</label>
        <textarea id="rqOther" rows="3" placeholder="e.g. roll-away bed, late check-in…">${esc(cur.other)}</textarea>
        <div class="rv-newtotal rv-newtotal--warn" id="rqErr" role="alert" tabindex="-1" hidden></div>
        <div class="rv-form-foot">
          <button class="book-btn rv-btn" id="rqSave" type="button">Save requests</button>
          <button class="rv-btn rv-btn--ghost" id="rqDrop" type="button">Discard</button>
        </div>
      </div>`);

    const $ = (id) => document.getElementById(id);
    $('rqDrop').addEventListener('click', closeDrawer);
    $('rqSave').addEventListener('click', async () => {
      const pref = $('rqPref').value;
      const ada = [...drawerBody.querySelectorAll('.bk-ada input:checked')].map((i) => i.value);
      const other = $('rqOther').value.trim();
      const comment = [pref, ...ada, other].filter(Boolean).join(', '); // D5's packing, verbatim
      const save = $('rqSave');
      if (!beginInflight(save)) return;
      startBusy({ label: 'Saving your requests…', origin: save });
      const { ok, data } = await postJson(`${base}?handler=Requests&id=${resId}`,
        { comment, ada: ada.length > 0, loyalty: $('rqLoyalty').value.trim() });
      if (!ok) {
        stopBusy();
        endInflight(save);
        showErr($('rqErr'), errorText(data));
        return;
      }
      // Unlike Change/Guests, this path DOESN'T navigate — it closes the drawer and toasts in
      // place. So the busy overlay it started has no reload to hand off to; it must be stopped
      // here, or the spinner sits "Saving…" forever even though the save succeeded.
      stopBusy();
      closeDrawer();
      track('xp_ev_reservation_modify', { change: 'requests' });
      toast(`Requests for ${r.conf} saved — the hotel will see them at check-in, and a revised confirmation is on its way`);
    });
  };

  /* ── Cancel (E4) — the prototype's inline confirm bar; "really" hard-deletes ─ */
  const CANCEL_ERRORS = {
    ModifyCutoff: ERRORS.ModifyCutoff,
    CancelCutoff: ERRORS.CancelCutoff,
  };
  /* FB91: Cancel now lives in the card's More menu, so its trigger DISAPPEARS when the menu
     closes — focus would be orphaned on a removed-from-view button. Hand focus to the confirm
     bar's safe choice ("Keep it"), never to the destructive one, and hand it back to the menu
     trigger when the bar is dismissed. */
  // (A101) the Cancel / Keep it / Yes, cancel wiring moved into wireCards() below, so it re-runs after a refresh.

  /* ── Charges drawer (E5) — the charge ledger for the booked stay ─────────── */
  const openRates = async (resId) => {
    let data;
    try {
      data = await getJson(`${base}?handler=Rates&id=${resId}`);
    } catch {
      toast('Could not load rates — refresh and try again.');
      return;
    }
    // The drawer now renders the SAME ledger the attendee approved at checkout (shared module),
    // instead of a horizontal chip strip plus a one-line money box that restated the same figures
    // in a second shape. It also scales: the strip was only ever comfortable at three or four
    // nights. The engine's night rows carry the pre-promo rate, so a promo stay finally shows its
    // was/now pricing here — the matrix rows this drawer used to read had no such column.
    //
    // Nothing here can blank a night. The old strip printed "—" whenever the LIVE matrix called a
    // night sold out, which says nothing about a night you are already booked into and are being
    // charged for — and it fired hardest on exactly the stays it hurt most, since a long booking
    // is often what drove its own tightest night to zero. The engine's night lines carry no
    // sold-out concept at all, so that whole class of bug is gone by construction. Live
    // availability belongs to the Change drawer; this drawer is about what the stay costs.
    const room = data.room;
    if (!room) {
      // The reprice failed server-side (a rate-config gap, a room type that no longer prices).
      // Say so plainly rather than rendering an empty ledger that reads as "$0".
      openDrawer('Charges · ' + data.conf, `
        <div class="rv-form">
          <div class="rv-rates-head"><strong>${esc(data.hotelName)}</strong> · ${esc(data.roomName)}</div>
          <div class="rv-newtotal rv-newtotal--warn"><i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
            Charges aren't available for this reservation right now. Your confirmation email
            has the charges for your stay.</div>
        </div>`, { readBody: true });
      return;
    }
    const guests = Math.max(1, ...room.nights.map((n) => n.occupancy));
    // The deposit/balance pair travels beside `room`, not inside it, because it is the amount
    // actually authorized rather than an engine recompute (see the handler). Merge it in here so
    // the shared ledger renders one block, and the provenance stays legible on the wire.
    const ledger = { ...room, deposit: data.deposit, balance: data.balance };
    // User-directed 2026-09-30: the legend only exists when a deposit was taken — same test as the
    // ledger's own deposit row. With "Guaranteed to Credit Card" nothing was charged, so a line
    // about "the amount charged" and "what you were charged" has nothing to say; the ledger's
    // deposit-timing note is the footnote there.
    const legend = Number(data.deposit) > 0
      ? `<div class="rv-rates-legend">Your stay: ${dateHtml(new Date(data.checkin + 'T00:00:00'))} → ${dateHtml(new Date(data.checkout + 'T00:00:00'))}.
          Rates and taxes are shown at current rates for this stay; the deposit is the amount
          charged to your card. Your confirmation email is the record of what you were
          charged.</div>`
      : '';
    openDrawer('Charges · ' + data.conf, `
      <div class="rv-form">
        <div class="rv-rates-head"><strong>${esc(data.hotelName)}</strong> · ${esc(data.roomName)}</div>
        ${renderChargesLedger(ledger, {
          badge: '<i class="fa-solid fa-receipt" aria-hidden="true"></i>',
          title: 'Summary of charges',
          guests,
          totalLabel: 'Total for stay',
          showDeposit: true,
          depositNote: data.depositRule === 'NoDeposit',   // same gate as checkout's drawer
          solo: true,
          locale: 'en-US',   // this page's money2 pins en-US — keep the drawer identical to it
        })}
        ${legend}
      </div>`);
  };

  /* ── Resend confirmation (E5) — recipients resolved server-side from the grant ─ */
  const resend = async (resId) => {
    // wireBusy already blocks re-entry (data-busy) and marks the trigger aria-disabled; disabling
    // the item here only dropped focus to <body> mid-menu (a11y audit 2026-09-17).
    const { ok, data } = await postJson(`${base}?handler=Resend&id=${resId}`);
    if (ok) track('xp_ev_confirmation_resend', {});
    toast(ok
      ? `Confirmation sent to ${[...(data.to ?? [])].join(', ')}`
      : errorText(data));
  };

  /* ── Add to calendar (E5) ────────────────────────────────────────────────────
     The .ics is BUILT AND SERVED by the page (?handler=Ics) rather than assembled here and
     handed to an <a download> on a blob: URL. The blob version was a download, and iOS has no
     general download-to-app route — Safari filed it under Files ▸ Downloads and an in-app
     browser dropped it without a word, so on an iPhone "Add to calendar" never reached Calendar
     (user report, 2026-08-21). A navigation to a `text/calendar` response is what iOS recognises
     as an event and opens the Add Event sheet for; desktop browsers cannot render that type, so
     they save the named file exactly as before.

     Assigning location.href (not window.open) keeps this inside the user's tap — no popup
     blocker to trip — and a Content-Disposition response does not unload the portal page. */
  const addToCalendar = (resId) => {
    // A text/calendar response does not unload the portal, so a plain push is safe here.
    track('xp_ev_calendar_download', { source: 'portal' });
    window.location.href = `${base}?handler=Ics&id=${resId}`;
  };

  /* ── Update payment (E5) — our own raw card fields (2026-08-12 posture) ────────
     This drawer was the LAST surface still using Stripe Elements; it now posts the raw PAN/exp/CVC
     (+ optional billing ZIP for AVS) to the Pay handler, exactly like checkout, and the gateway
     builds the Stripe PaymentMethod server-side. Stripe.js is no longer loaded here: the Pay
     command treats a 3DS `requires_action` as a CardError (no client challenge surface in a
     drawer — same posture as E3's difference-auth), so there is nothing for Stripe.js to do. Card
     inputs are never rendered back and never logged. */
  const openPay = async (resId, sharedCount = 1) => {
    let info;
    try {
      info = await getJson(`${base}?handler=PayInfo&id=${resId}`);
    } catch {
      toast('Could not load the payment form — refresh and try again.');
      return;
    }
    // publishableKey doubles as the "Stripe is configured for this event" signal (PayInfo returns
    // it from the processor row); no card can authorize without a processor.
    if (!info.publishableKey) {
      toast('Payments are not configured for this event.');
      return;
    }
    /* FB90: name the card being replaced, and — when it is the ACCOUNT's card — say how many
       reservations this one update moves. The count rides on the trigger (the server already
       counted them when it rendered the page), so the drawer needs no extra round-trip. */
    const shared = sharedCount;
    const current = info.cardLast4
      ? `Currently on file: <strong>&bull;&bull;&bull;&bull; ${info.cardLast4}</strong>${info.cardHolder ? ` · ${info.cardHolder}` : ''}.`
      : '';
    const reach = info.isAccountCard
      ? `<div class="bk-occ-note bk-occ-note--warn"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
           This is the card on your <strong>account</strong>${shared > 1 ? ` — it guarantees all ${shared} of your reservations` : ''}.
           Replacing it moves ${shared > 1 ? 'every one of them' : 'this reservation'} to the new card.</div>`
      : '';
    /* The account card's drawer must NOT wear one reservation's confirmation number or one room's
       deposit figure: the reservation id is only the audit anchor the command writes history
       against, and the card covers every room on the account. A per-reservation card (FB59) keeps
       the original, correctly specific copy. */
    const held = info.isAccountCard
      ? `This card guarantees your reservations and holds their deposits.`
      : `The card on file guarantees the room and holds the deposit${info.deposit > 0 ? ` (${money2(info.deposit)})` : ''}.`;
    openDrawer(info.isAccountCard ? 'Update card on account' : 'Update payment · ' + info.conf, `
      <div class="rv-form">
        <div class="bk-occ-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i> ${current} ${held} The new card is verified with a hold; nothing is charged.</div>
        ${reach}
        <div class="rv-form-2col">
          <span class="bk-float"><input type="text" id="upFirst" placeholder=" " autocomplete="cc-given-name" aria-required="true" aria-describedby="upNameErr" aria-label="First name on card (required)"><label aria-hidden="true">First name on card</label></span>
          <span class="bk-float"><input type="text" id="upLast" placeholder=" " autocomplete="cc-family-name" aria-required="true" aria-describedby="upNameErr" aria-label="Last name on card (required)"><label aria-hidden="true">Last name on card</label></span>
        </div>
        <span class="bk-field-err" id="upNameErr"></span>
        <label class="bk-fieldlabel" id="upCardLbl">Card details</label>
        <div class="bk-grid" role="group" aria-labelledby="upCardLbl">
          <span class="bk-float bk-span2">
            <input type="text" id="upCard" class="bk-cardnum" placeholder=" " inputmode="numeric" autocomplete="off" maxlength="23" aria-required="true" aria-describedby="upCardErr" aria-label="Card number (required)">
            <label aria-hidden="true">Card number *</label>
          </span>
          <span class="bk-field-err bk-span2" id="upCardErr"></span>
          <div class="bk-grid bk-grid--third bk-span2">
            <span class="bk-float">
              <input type="text" id="upExp" class="bk-exp" placeholder=" " inputmode="numeric" autocomplete="off" maxlength="5" aria-required="true" aria-describedby="upExpErr" aria-label="Card expiry (required, format M M slash Y Y)">
              <label aria-hidden="true">MM/YY *</label>
            </span>
            <span class="bk-float">
              <input type="text" id="upCvc" class="bk-cvv" placeholder=" " inputmode="numeric" autocomplete="off" maxlength="4" aria-required="true" aria-describedby="upCvcErr" aria-label="Card security code (required)">
              <label aria-hidden="true">CVV *</label>
            </span>
            <span class="bk-float">
              <input type="text" id="upZip" placeholder=" " inputmode="numeric" autocomplete="billing postal-code" maxlength="10" aria-label="Billing zip (optional)">
              <label aria-hidden="true">Billing zip</label>
            </span>
          </div>
          <span class="bk-field-err bk-span2" id="upExpErr"></span>
          <span class="bk-field-err bk-span2" id="upCvcErr"></span>
        </div>
        <div class="rv-newtotal rv-newtotal--warn" id="upErr" role="alert" tabindex="-1" hidden></div>
        <div class="rv-form-foot">
          <button class="book-btn rv-btn" id="upSave" type="button">Save card</button>
          <button class="rv-btn rv-btn--ghost" id="upDrop" type="button">Discard</button>
        </div>
      </div>`);

    const $ = (id) => document.getElementById(id);
    const cardEl = $('upCard'), expEl = $('upExp'), cvcEl = $('upCvc');
    const cardDigits = () => cardEl.value.replace(/\D/g, '');
    /* Save used to be `disabled` until the number, MM/YY and CVC parsed — so with a mistyped expiry
       the button simply was not in the Tab order and nothing said why (a11y audit 2026-09-17). It
       is always enabled now and the same rules run on click, per field: text under the field,
       aria-invalid, and focus on the first one wrong. The server and Stripe remain the authority;
       ZIP is optional (AVS aid), matching checkout. */
    const flag = (el, errEl, msg) => {
      errEl.textContent = msg || '';
      el.classList.toggle('bk-missing', !!msg);
      if (msg) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid');
      return !!msg;
    };
    const validate = () => {
      const first = $('upFirst'), last = $('upLast');
      const bad = [];
      // The name pair shares one message line, but ONLY the blank half is marked invalid — a
      // filled first name must not read as wrong because the last name is empty.
      const firstMissing = !first.value.trim(), lastMissing = !last.value.trim();
      flag(first, $('upNameErr'), firstMissing ? 'Enter the name on the card.' : '');
      flag(last, $('upNameErr'), lastMissing ? 'Enter the name on the card.' : '');
      if (firstMissing) bad.push(first); else if (lastMissing) bad.push(last);
      if (flag(cardEl, $('upCardErr'), cardDigits().length >= 12 ? '' : 'Enter the card number.')) bad.push(cardEl);
      if (flag(expEl, $('upExpErr'), /^\d{2}\/\d{2}$/.test(expEl.value.trim()) ? '' : 'Enter the expiry as MM/YY.')) bad.push(expEl);
      if (flag(cvcEl, $('upCvcErr'), cvcEl.value.replace(/\D/g, '').length >= 3 ? '' : 'Enter the security code.')) bad.push(cvcEl);
      return bad;
    };
    // A flagged field clears the moment it is answered — the red state describes the form NOW.
    const clearOnInput = (el, errEl) => el.addEventListener('input', () => {
      if (el.getAttribute('aria-invalid') === 'true') flag(el, errEl, '');
    });
    clearOnInput($('upFirst'), $('upNameErr'));
    clearOnInput($('upLast'), $('upNameErr'));
    // Light input hygiene mirroring checkout: digits only, and auto-slash the expiry as MM/YY.
    cardEl.addEventListener('input', () => {
      const d = cardDigits().slice(0, 19);
      cardEl.value = d.replace(/(\d{4})(?=\d)/g, '$1 ').trim(); // space-group; server strips spaces
    });
    expEl.addEventListener('input', () => {
      let v = expEl.value.replace(/\D/g, '').slice(0, 4);
      if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
      expEl.value = v;
    });
    cvcEl.addEventListener('input', () => { cvcEl.value = cvcEl.value.replace(/\D/g, '').slice(0, 4); });
    clearOnInput(cardEl, $('upCardErr'));
    clearOnInput(expEl, $('upExpErr'));
    clearOnInput(cvcEl, $('upCvcErr'));

    $('upDrop').addEventListener('click', closeDrawer);
    $('upSave').addEventListener('click', async () => {
      const err = $('upErr');
      hideErr(err);
      const bad = validate();
      if (bad.length) {
        // Moving focus reads the field's label + its describedby message. Only when focus is
        // ALREADY there (nothing would be read) does the message go through the announcer.
        const target = bad[0];
        const msgId = target.getAttribute('aria-describedby');
        if (document.activeElement === target) announcePage(document.getElementById(msgId)?.textContent ?? '');
        else target.focus();
        return;
      }
      const first = $('upFirst').value.trim();
      const last = $('upLast').value.trim();
      const save = $('upSave');
      if (!beginInflight(save)) return;
      // The longest wait on this page: the server builds the Stripe PaymentMethod and authorizes.
      startBusy({ label: 'Checking your card…', origin: save });
      const { ok, data } = await postJson(`${base}?handler=Pay&id=${resId}`, {
        cardNumber: cardEl.value, cardExpiry: expEl.value, cvc: cvcEl.value,
        postalCode: $('upZip').value.trim(), first, last,
      });
      if (!ok) {
        stopBusy();
        endInflight(save);
        showErr(err, data?.error === 'CardError'
          ? 'That card could not be verified — check the details and try again.'
          : errorText(data));
        return;
      }
      reloadWithToast(info.isAccountCard && shared > 1
        ? `Card ****${data.cardLast4 ?? ''} now guarantees all ${shared} reservations`
        : `Card ****${data.cardLast4 ?? ''} now guarantees ${info.conf}`, modified('card'));
    });
  };

  /* ── Contact info (E5 / M7) — account-level, from More options ─────────────────
     M7 (2026-08-12 feedback): the drawer now edits the FULL booking contact (title, company,
     address, state/country, zip, alt phone), not just name/email/phone — the fields the visitor
     entered at booking. State/country reuse checkout's combobox.js typeahead over the same option
     lists, so an off-list value can't reach the geo-resolving write chain. The US posts a state
     CODE (hidden twin); an international address posts free-text into the same slot. */
  const US = 'United States';
  const openContact = async () => {
    let c;
    try {
      c = await getJson(`${base}?handler=ContactInfo`);
    } catch {
      toast('Could not load contact info — refresh and try again.');
      return;
    }
    const who = [c.first, c.last].filter(Boolean).join(' ');
    const isUs = (c.country || US) === US;
    const stateName = (c.states || []).find((s) => s.code === c.stateProvince)?.name ?? '';
    const stateOpts = (c.states || []).map((s) => `<option value="${esc(s.code)}">${esc(s.name)}</option>`).join('');
    const countryOpts = (c.countries || []).map((co) => `<option value="${esc(co.name)}"></option>`).join('');

    openDrawer('Contact info · ' + who, `
      <div class="rv-form">
        <div class="bk-occ-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i> The booking contact books the reservations and receives every confirmation${c.company ? ` for <strong>${esc(c.company)}</strong>` : ''}. These are the details you entered at booking.</div>
        <span class="bk-float"><input type="text" id="ciTitle" maxlength="64" placeholder=" " aria-label="Title (optional)" value="${esc(c.title)}"><label aria-hidden="true">Title (optional)</label></span>
        <div class="rv-form-2col">
          <span class="bk-float"><input type="text" id="ciFirst" maxlength="64" placeholder=" " autocomplete="given-name" aria-required="true" aria-label="First name (required)" value="${esc(c.first)}"><label aria-hidden="true">First *</label></span>
          <span class="bk-float"><input type="text" id="ciLast" maxlength="64" placeholder=" " autocomplete="family-name" aria-required="true" aria-label="Last name (required)" value="${esc(c.last)}"><label aria-hidden="true">Last *</label></span>
        </div>
        <span class="bk-float"><input type="text" id="ciCompany" maxlength="255" placeholder=" " autocomplete="organization" aria-required="true" aria-label="Company (required)" value="${esc(c.company)}"><label aria-hidden="true">Company *</label></span>
        <span class="bk-float"><input type="text" id="ciAddr1" maxlength="255" placeholder=" " autocomplete="address-line1" aria-required="true" aria-label="Address 1 (required)" value="${esc(c.address1)}"><label aria-hidden="true">Address 1 *</label></span>
        <span class="bk-float"><input type="text" id="ciAddr2" maxlength="255" placeholder=" " autocomplete="address-line2" aria-label="Address 2 (optional)" value="${esc(c.address2)}"><label aria-hidden="true">Address 2 (optional)</label></span>
        <span class="bk-float"><input type="text" id="ciCity" maxlength="255" placeholder=" " autocomplete="address-level2" aria-required="true" aria-label="City (required)" value="${esc(c.city)}"><label aria-hidden="true">City *</label></span>
        <div class="rv-form-2col">
          <span class="bk-float">
            <input type="text" id="ciStateDisplay" list="ciStateList" maxlength="50" placeholder=" " autocomplete="off" aria-required="true" aria-describedby="ciStateErr" aria-label="State (required)" value="${esc(stateName)}"${isUs ? '' : ' hidden disabled'}>
            <input type="hidden" id="ciState" value="${isUs ? esc(c.stateProvince) : ''}"${isUs ? '' : ' disabled'}>
            <datalist id="ciStateList">${stateOpts}</datalist>
            <input type="text" id="ciProvince" maxlength="50" placeholder=" " autocomplete="address-level1" aria-required="true" aria-label="State or province (required)" value="${isUs ? '' : esc(c.stateProvince)}"${isUs ? ' hidden disabled' : ''}>
            <label aria-hidden="true">State/Province *</label>
          </span>
          <span class="bk-float"><input type="text" id="ciZip" maxlength="50" placeholder=" " autocomplete="postal-code" aria-required="true" aria-label="Zip (required)" value="${esc(c.zip)}"><label aria-hidden="true">Zip *</label></span>
        </div>
        <span class="bk-field-err" id="ciStateErr"></span>
        <span class="bk-float"><input type="text" id="ciCountry" list="ciCountryList" placeholder=" " autocomplete="off" aria-required="true" aria-describedby="ciCountryErr" aria-label="Country (required)" value="${esc(c.country || US)}"><datalist id="ciCountryList">${countryOpts}</datalist><label aria-hidden="true">Country *</label></span>
        <span class="bk-field-err" id="ciCountryErr"></span>
        <div class="rv-form-2col">
          <span class="bk-float"><input type="tel" id="ciPhone" maxlength="64" placeholder=" " autocomplete="tel" aria-required="true" aria-label="Phone (required)" value="${esc(c.phone)}"><label aria-hidden="true">Phone *</label></span>
          <span class="bk-float"><input type="tel" id="ciAltPhone" maxlength="64" placeholder=" " autocomplete="tel" aria-label="Alternate phone (optional)" value="${esc(c.altPhone)}"><label aria-hidden="true">Alt phone (optional)</label></span>
        </div>
        <span class="bk-float"><input type="email" id="ciEmail" maxlength="255" placeholder=" " autocomplete="email" aria-required="true" aria-label="Email (required) — receives all confirmations and signs in here" value="${esc(c.email)}"><label aria-hidden="true">Email * — confirmations &amp; sign-in</label></span>
        <div class="rv-newtotal rv-newtotal--warn" id="ciErr" role="alert" tabindex="-1" hidden></div>
        <div class="rv-form-foot">
          <button class="book-btn rv-btn" id="ciSave" type="button">Save changes</button>
          <button class="rv-btn rv-btn--ghost" id="ciDrop" type="button">Discard</button>
        </div>
      </div>`);

    const $ = (id) => document.getElementById(id);
    // State combobox (US, code↔name split) + country combobox (single input). combobox.js wraps the
    // state display input in a .bk-combo; the province free-text twin lives beside it.
    const stateCombo = createCombobox($('ciStateDisplay'), {
      valueEl: $('ciState'), errEl: $('ciStateErr'), invalidMessage: 'Choose a state from the list.',
    });
    createCombobox($('ciCountry'), { errEl: $('ciCountryErr'), invalidMessage: 'Choose a country from the list.' });
    const stateWrap = $('ciStateDisplay').closest('.bk-combo');
    // Swap the state control on country change (checkout's syncState, condensed): exactly one of the
    // combobox / free-text province is enabled, so exactly one value is read on save.
    const syncState = () => {
      const us = $('ciCountry').value.trim() === US;
      if (stateWrap) stateWrap.hidden = !us;
      $('ciStateDisplay').disabled = !us; $('ciState').disabled = !us;
      $('ciProvince').hidden = us; $('ciProvince').disabled = us;
    };
    $('ciCountry').addEventListener('change', syncState);
    syncState();

    $('ciDrop').addEventListener('click', closeDrawer);
    $('ciSave').addEventListener('click', async () => {
      const us = $('ciCountry').value.trim() === US;
      const save = $('ciSave');
      const err = $('ciErr');
      const stateProvince = us ? $('ciState').value.trim() : $('ciProvince').value.trim();
      if (!beginInflight(save)) return;
      hideErr(err);
      startBusy({ label: 'Saving your contact info…', origin: save });
      const { ok, data } = await postJson(`${base}?handler=Contact`, {
        title: $('ciTitle').value.trim(),
        first: $('ciFirst').value.trim(),
        last: $('ciLast').value.trim(),
        company: $('ciCompany').value.trim(),
        address1: $('ciAddr1').value.trim(),
        address2: $('ciAddr2').value.trim(),
        city: $('ciCity').value.trim(),
        stateProvince,
        zip: $('ciZip').value.trim(),
        country: $('ciCountry').value.trim(),
        phone: $('ciPhone').value.trim(),
        altPhone: $('ciAltPhone').value.trim(),
        email: $('ciEmail').value.trim(),
      });
      if (!ok) {
        stopBusy();
        endInflight(save);
        showErr(err, data?.error === 'bad-spec'
          ? 'Check the required fields — name, company, address, city, state, zip, country, phone and email.'
          : data?.error === 'ContactNotAcceptable'
            ? 'Check the state, country and (for US) a 10-digit phone number.'
            : errorText(data));
        return;
      }
      reloadWithToast(data.emailChanged
        ? 'Contact info updated — sign in again with your new email to keep managing bookings'
        : `Contact info for ${who} updated`, modified('contact'));
    });
  };

  /* wire the card buttons. FB33: every opener fetches before its drawer appears, so the
     clicked control spins (icon swap + aria-busy) until the await settles — a click never
     reads as a dead one. Re-entry is blocked while busy. */
  /* The spinner here used to be hand-rolled: swap the button's <i> for fa-spin, swap it back in a
     finally. Two holes, both of which read to the user as "the click did nothing":

       1. `if (icon)` — a control with NO glyph got no feedback whatsoever. Every button inside the
          drawers is text-only, so the slowest actions on the page (Save changes, Update payment)
          were the silent ones.
       2. It never entered the shared busy state, so none of FB71's later stages could fire. A menu
          fetch that takes four seconds showed a 12px spinner inside a menu that had just closed
          itself — no bar, no named pill, no escalation copy, and nothing stopping a second click
          from starting a competing navigation.

     busy.js already solves both (markOrigin handles the no-glyph case by overlaying the spinner on
     a transparent label, so the box cannot reflow), and the ramp means a fast action is still
     completely silent. `origin` is explicit rather than inferred because wireDrawer hands the busy
     state to a DIFFERENT element than the one clicked — see below. */
  const wireBusy = (selector, handler, { label, prepare } = {}) =>
    portal.querySelectorAll(selector).forEach((b) => {
    b.addEventListener('click', async () => {
      if (b.dataset.busy) return;
      /* FB91: remembered at click time because a handler may BLUR this button before we get to
         close the menu (busy.js marks it aria-disabled; nothing disables it any more, but the
         menu is hidden by closeMenu below). By then activeElement no longer tells us the user was
         ever here. */
      const hadFocus = document.activeElement === b;
      b.dataset.busy = '1';
      // `prepare` runs BEFORE the busy state so it can redirect the spinner: busy.js binds its
      // origin once per session, so an element chosen afterwards would never receive it.
      const origin = prepare ? prepare(b) : b;
      startBusy({ label, origin });
      try {
        await handler(b);
      } finally {
        delete b.dataset.busy;
        // A handler that navigated has already started the busy state for the page it is loading.
        if (!navigating) stopBusy();
        /* FB91: a menu item closes its OWN menu when the action SETTLES, not when it is clicked —
           this spinner is the only feedback "Email confirmation" gives, and closing on click
           would hide the one thing telling the user the click landed. Closing only its own menu
           matters because the user is free to open another card's menu while this one is in
           flight; closing all of them would snap that one shut under their cursor. */
        const own = menuFor(b);
        if (own && !own.menu.hidden) {   // still hidden here means the handler already closed it
          if (hadFocus && !own.menu.contains(document.activeElement)) own.btn.focus();
          closeMenu(own);
        }
      }
    });
  });
  /* FB92: FB91 needed this for [data-pay] alone; 2a moved three more drawer-openers into the menu,
     so it is now the RULE rather than an exception —

         opens a drawer  ->  close the menu FIRST
         returns a toast ->  close on settle (wireBusy's finally), so the spinner stays visible

     The drawer's focus trap captures document.activeElement when it activates (focus-trap.js:66)
     and re-focuses it on close. If the item is still inside an open menu at that moment, the trap's
     restore target is a button that is display:none by the time the drawer closes, and focus is
     lost entirely. Closing first hands focus to the trigger, which is visible and stays that way.
     Harmless for [data-change], which lives outside any menu — menuFor returns null. */
  /* FB92: closing the menu hides the item that was clicked, so the busy state has to move to the
     TRIGGER, which stays on screen — otherwise "View charges" is silent for its whole round
     trip, breaking FB33's "a click never reads as a dead one" for exactly the items that moved
     into the menu. That is all `prepare` does here; wireBusy owns the rest.
     [data-change] lives outside any menu — menuFor returns null and the clicked button keeps it. */
  const wireDrawer = (selector, handler, label) => wireBusy(selector, handler, {
    label,
    prepare: (b) => {
      const own = menuFor(b);
      if (!own) return b;
      closeMenu(own);
      return own.btn;
    },
  });
  /* A101: everything wired per card — the Cancel confirm bar, the drawer openers, the busy buttons — lives
     here so it runs again after the cards are refreshed in place (refreshPortal). */
  const wireCards = () => {
  portal.querySelectorAll('[data-cancel]').forEach((b) =>
    b.addEventListener('click', () => {
      const bar = document.getElementById('cx-' + b.dataset.cancel);
      if (!bar) return;
      bar.hidden = false;
      closeAllMenus();
      bar.querySelector('[data-keep]')?.focus();
    }));
  portal.querySelectorAll('[data-keep]').forEach((b) =>
    b.addEventListener('click', () => {
      const bar = document.getElementById('cx-' + b.dataset.keep);
      if (!bar) return;
      bar.hidden = true;
      bar.closest('.rv-res')?.querySelector('.rv-more button[aria-controls]')?.focus();
    }));
  portal.querySelectorAll('[data-really]').forEach((b) =>
    b.addEventListener('click', async () => {
      // Not `disabled` (that dropped focus to <body> for the round trip and left it there on a
      // refusal — a11y audit 2026-09-17); the in-flight flag guards the double click.
      if (!beginInflight(b)) return;
      startBusy({ label: 'Cancelling your reservation…', origin: b });
      const { ok, data } = await postJson(`${base}?handler=Cancel&id=${Number(b.dataset.really)}`);
      if (!ok) {
        stopBusy();
        endInflight(b);
        toast(CANCEL_ERRORS[data?.error] ?? errorText(data));
        return;
      }
      reloadWithToast(`Reservation ${data.acknowledgementNumber} cancelled` +
        (data.authVoided ? ' — your deposit hold is released' : ''),
        { event: 'xp_ev_reservation_cancel', data: { depositReleased: !!data.authVoided } });
    }));
  wireDrawer('[data-change]', (b) => openChange(Number(b.dataset.change)), 'Loading your reservation…');
  wireDrawer('[data-guests]', (b) => openGuests(Number(b.dataset.guests)), 'Loading room guests…');
  wireDrawer('[data-reqs]', (b) => openRequests(Number(b.dataset.reqs)), 'Loading your requests…');
  wireDrawer('[data-rates]', (b) => openRates(Number(b.dataset.rates)), 'Loading charges…');
  /* FB90: the account-card trigger lives in the toolbar's More options and carries how many
     reservations the one card guarantees; a per-reservation card (FB59 split checkout) reports a
     count of 1. */
  wireDrawer('[data-pay]', (b) => openPay(Number(b.dataset.pay), Number(b.dataset.acctCount || 1)),
    'Loading your card…');
  /* Contact info was the one opener wired by hand, so it fetched with no feedback of any kind —
     same drawer shape as the four above, so it gets the same treatment. */
  wireDrawer('[data-contact]', () => openContact(), 'Loading contact info…');
  /* These two return a toast / a download rather than a drawer, so they keep close-on-settle. */
  wireBusy('[data-send]', (b) => resend(Number(b.dataset.send)), { label: 'Sending your confirmation…' });
  wireBusy('[data-ics]', (b) => addToCalendar(Number(b.dataset.ics)), { label: 'Building your calendar file…' });
  };
  wireCards();
  wireMenus(portal);

  /* ── Epic H (H3b task 7 → A98 → A101): the booking assistant changed a reservation through this page's
     own handlers (date change, guests, requests, cancel, resend), or signed the attendee in or out. The
     cards are server-rendered, so the freshest markup is the page itself: fetch it again, move the new
     portal children in, re-run the per-card wiring, say so. No reload (A101, user 2026-10-30: "user
     wouldn't know why it refreshed") — the chat panel is not touched, its toast stays where it is.
     A reload remains the fallback: the fetch failed, or the portal is gone from the page (a sign-out),
     where the sign-in form is the whole page and the drawer wiring has nothing to hold. */
  const fallbackReload = (label) => {
    if (navigating) return;
    navigating = true;
    document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label } }));
    window.location.replace(base + window.location.search);
  };
  const refreshPortal = async (said) => {
    const doc = await fetchPage(base + window.location.search);
    const fresh = doc?.querySelector('[data-rv-portal]');
    if (!fresh) {
      fallbackReload('Refreshing your reservations…');
      return;
    }
    closeDrawer();
    closeAllMenus();
    portal.replaceChildren(...fresh.childNodes);
    wireMenus(portal);
    wireCards();
    announcePage(said);
  };
  document.addEventListener('ebk:reservation-changed', () => { void refreshPortal('Your reservations are updated.'); });
  document.addEventListener('ebk:identity-changed', (e) => {
    if (e.detail?.reason === 'signout') fallbackReload('Signing you out…');
    else void refreshPortal('You are signed in. Your reservations are shown.');
  });

  /* ── Epic H (H3 task 9, interim until H3b): the booking assistant's reservation card deep-links
     here as /{guid}/reservations?action=change|guests|add|cancel&res={id}. Open the matching drawer
     (or the cancel confirm bar) for that reservation once the portal has wired up, then drop
     `action` so a reload does not reopen it. `res` stays: the server renders only that reservation
     for it (the chat's "Change room", user 2026-10-12), and a reload keeps that view. Every action is re-checked against what the
     card actually offers — a closed modify/cancel window removed the control, and then the
     answer is the same sentence the control's absence implies, not a silent no-op. The destructive
     one stops at the confirm bar ("Keep it" is focused), exactly like clicking Cancel by hand. */
  const deep = new URLSearchParams(window.location.search);
  const deepAction = deep.get('action');
  const deepRes = /^\d+$/.test(deep.get('res') ?? '') ? Number(deep.get('res')) : 0;
  if (deepRes) {
    deep.delete('action');
    const rest = deep.toString();
    window.history.replaceState(null, '', base + (rest ? '?' + rest : ''));

    const card = portal.querySelector(`.rv-res[data-res="${deepRes}"]`);
    const control = {
      change: `[data-change="${deepRes}"]`,
      guests: `[data-guests="${deepRes}"]`,
      cancel: `[data-cancel="${deepRes}"]`,
    }[deepAction ?? ''];
    const refused = {
      change: 'Dates for this reservation can no longer be changed online — contact the housing desk.',
      guests: 'Guests for this reservation can no longer be changed online — contact the housing desk.',
      cancel: 'This reservation can no longer be cancelled online — contact the housing desk.',
      add: 'To add a room, choose one on the event page and book it with the same email.',
    };
    const openFromChat = () => {
      if (!card) {
        toast('I could not find that reservation on this page.');
        return;
      }
      card.scrollIntoView({ block: 'center' });
      const target = control ? portal.querySelector(control) : null;
      if (target) {
        target.click(); // the real handler: busy feedback, focus trap and restore included
      } else if (deepAction && refused[deepAction]) {
        toast(refused[deepAction]);
      }
    };
    if (document.readyState === 'complete') openFromChat();
    else window.addEventListener('load', openFromChat, { once: true });
  }
}
return true;
}
let portalReady = initPortal();

/* A101: the page arrived anonymous (the sign-in form) and the attendee signed in through the chat — the
   fetched page now carries the portal, the drawer and the backdrop inside <main>. Move them in and wire
   the portal; the chat panel (outside <main>) is untouched. A sign-out on the form page changes nothing. */
document.addEventListener('ebk:identity-changed', async (e) => {
  if (portalReady || e.detail?.reason !== 'signin') return;
  const doc = await fetchPage(window.location.href);
  const main = document.getElementById('main');
  const freshMain = doc?.getElementById('main');
  if (!main || !freshMain?.querySelector('[data-rv-portal]')) {
    document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label: 'Signing you in…' } }));
    pageNavigation.reload();
    return;
  }
  main.replaceChildren(...freshMain.childNodes);
  portalReady = initPortal();
  announcePage('You are signed in. Your reservations are shown.');
});
